/* ============================================================================
   Plain values, importable without pulling in Zod.
   ----------------------------------------------------------------------------
   Everything here is data or a pure function: no schema, no `z.` at all. The
   schema modules import FROM this file, so each value still has exactly one
   definition — it just lives where a browser can reach it cheaply.

   Why this exists: the package index re-exports every schema module, so
   importing ANY runtime value from `@mys/contracts` evaluates all of them and
   drags Zod into the bundle. The admin dashboard did that for three constants
   and paid 29 KB gzipped for it. Import shared constants from
   `@mys/contracts/constants` in browser code; the API can use either.
   ========================================================================== */
/* --------------------------------------------------- roles and rights ----
   Who an admin is, and what that lets them do. docs/ADMIN-ROADMAP.md 1.3.

   `owner` is the specification's Super Admin and the name does not change:
   every owner-only rule in CLAUDE.md — invoice settlement, the team, the
   business details — is written against it. `staff` predates the other four
   and is kept because live accounts carry it.

   Here rather than in auth.schema.ts because the dashboard's nav, its action
   buttons and its invite form all read this AT RUNTIME, and a runtime import
   from a schema module drags Zod into the bundle — 29 KB gzipped, which this
   repo has already paid once. auth.schema.ts imports ADMIN_ROLES from here
   and wraps it in `z.enum`. */
/**
 * The six roles that SHIPPED. A deployment may have more.
 *
 * Since slice 1.4 roles are rows in `Role`, so this list is no longer the
 * answer to "what roles exist" — it is the set the migration seeded and the
 * set that cannot be deleted. Ask the database for the current list; use this
 * for the seed, for the labels below, and for anything that must hold whatever
 * the matrix is edited to.
 */
export const ADMIN_ROLES = ['owner', 'staff', 'sales', 'developer', 'accounts', 'support'];
/** What a BUILT-IN role is called on screen. A custom role carries its own
    label in `Role.label`, so anything rendering a role reads the label from
    the API and falls back to this only for the six. */
export const ADMIN_ROLE_LABELS = {
    owner: 'Owner',
    staff: 'Staff',
    sales: 'Sales',
    developer: 'Developer',
    accounts: 'Accounts',
    support: 'Support',
};
/**
 * Every permission the API enforces, as `module:action`.
 *
 * **This enumerates actions that EXIST — it is not a
 * view/create/edit/delete/export cross-product.** The specification asks for
 * the full matrix; a key with no route behind it is a switch that silently
 * governs nothing, and 1.4 turns this list into editable database rows, so an
 * invented key becomes an invented row with a tick beside it. Add the
 * permission in the same change as the endpoint.
 *
 * The module half is the dashboard's own URL segment — `care-plans`, not
 * `carePlans` — because the nav filters itself on `<module>:view`.
 *
 * **Adding one here grants it to NOBODY, not even the owner.** Since slice
 * 1.4a the grants are rows, so a new permission needs a migration that inserts
 * it for whoever should have it — and until then every route behind it answers
 * 403 to everyone. That is not left to memory: `ROLE_PERMISSIONS.owner` is
 * this list, so adding a name here makes `tests/roles.test.ts` disagree with
 * the database and turn red. Write the migration in the same change.
 */
/**
 * What a client pays somebody else for. Slice 1.8b.
 *
 * In `constants.ts` rather than beside its schema for the usual reason: the
 * dashboard renders these as a dropdown at runtime, and one runtime import
 * from the package index drags Zod into the bundle. CLAUDE.md.
 *
 * `other` is there so a record nobody anticipated does not become free text
 * in the `name` field — "domain (actually a Shopify plan)" is how a grouped
 * report stops grouping.
 */
export const CLIENT_SERVICE_KINDS = ['domain', 'hosting', 'email', 'ssl', 'other'];
/** Hinglish, because this reaches a screen the owner reads daily. */
export const CLIENT_SERVICE_KIND_LABELS = {
    domain: 'Domain',
    hosting: 'Hosting',
    email: 'Email',
    ssl: 'SSL',
    other: 'Other',
};
export const PERMISSIONS = [
    'leads:view',
    'leads:edit',
    'quotes:view',
    'quotes:create',
    'quotes:send',
    'clients:view',
    'clients:create',
    /** Editing a client, and the domain and hosting records hanging off it:
        what renews, when, and with which provider. Not owner-only — whoever
        answers the phone when a domain is about to lapse needs to fix it. */
    'clients:edit',
    /** Storing a hosting or registrar login, and REVEALING one. Owner-only,
        and the only permission here that governs reading rather than writing:
        a credential is the one thing in this database that is worth stealing
        on its own. Every reveal is recorded. */
    'clients:secrets',
    'projects:view',
    'projects:create',
    'projects:edit',
    'invoices:view',
    'invoices:create',
    /** Recording cash, UPI or a bank transfer. Owner-only, and not a discount
        button: it takes no amount and no date. CLAUDE.md. */
    'invoices:settle',
    'care-plans:view',
    'care-plans:create',
    'care-plans:edit',
    'edit-requests:view',
    'edit-requests:edit',
    'posts:view',
    'posts:create',
    'posts:edit',
    'posts:delete',
    'testimonials:view',
    'testimonials:approve',
    'analytics:view',
    /** Everybody sees their OWN login history without a permission; this is
        seeing everyone's. */
    'security:view-all',
    'settings:business',
    /** Inviting, changing a colleague's role, and resetting their two-factor. */
    'settings:team',
];
/**
 * The owner's alone, and the one list in here that is written by hand.
 *
 * **This list is not a new decision — it is a photograph of the authority that
 * already existed.** Every route named here was `requireRole('owner')` before
 * slice 1.3, and all of them keep that call as well as a permission check: two
 * gates rather than one, because a wrong line in a data structure is a quiet
 * widening and a role NAME is hard to widen by accident.
 * `tests/permissions.test.ts` asserts the map and the routes agree.
 *
 * It is also why `accounts` can read money and not yet bill. An accounts role
 * that raises invoices is probably what the specification wants, but raising
 * one is owner-only by a decision the owner took, so widening it is the
 * owner's to take too. CONTEXT.md §14.
 */
export const OWNER_ONLY_PERMISSIONS = [
    /* Raising one puts a number in a GST sequence; settling one is a signature
       on money that arrived. Both were owner-only before this map existed and
       both stay owner-only BECAUSE of that — see below. */
    'invoices:create',
    'invoices:settle',
    /* What a client is charged every month. Same reason. */
    'care-plans:create',
    'care-plans:edit',
    /* Everyone's login history, including the owner's. Anybody's own is free. */
    'security:view-all',
    'settings:business',
    'settings:team',
    /* A registrar login is the keys to a client's business, and unlike every
       other entry here the risk is in READING it, not in writing it. Owner-only
       by the same two gates as the rest: the permission AND requireRole, because
       a wrong line in the matrix is a quiet widening. — Note that clients:edit
       is deliberately NOT here: a domain that is about to lapse has to be
       fixable by whoever notices. */
    'clients:secrets',
];
const EXCEPT_OWNER_ONLY = PERMISSIONS.filter((permission) => !OWNER_ONLY_PERMISSIONS.includes(permission));
/**
 * **THE SEED, NOT THE ANSWER. Editing this changes nothing in a running
 * system.**
 *
 * Until slice 1.4 this map decided every permission check. It does not any
 * more: `20261012124500_roles_and_permissions` copied it into `Role` and
 * `RolePermission` rows, every check reads those rows, and the owner can edit
 * them from the dashboard. What this is now is the snapshot that migration
 * inserted — kept because a fresh database still has to start somewhere, and
 * because `tests/roles.test.ts` asserts the two still agree, which is what
 * catches a hand-edit to the migration or to this.
 *
 * To change what a role may do in a DEPLOYED system, edit the matrix. To
 * change what a FRESH database starts with, change this AND write a migration
 * — one without the other is a difference between two installations that
 * nothing reports.
 *
 * **`staff` is DERIVED on purpose.** Before 1.3 the role meant "everything
 * the owner-only routes do not guard", and live accounts carry it — so a
 * hand-written list here would be a silent removal of access the day somebody
 * forgets a line, and a new permission would default to refused for people who
 * had it a minute earlier. Derived, it keeps meaning exactly what it meant.
 * The four new roles are hand-written for the opposite reason: they are
 * deliberately narrow, and a new module should NOT appear in them until
 * somebody decides it should.
 */
export const ROLE_PERMISSIONS = {
    owner: PERMISSIONS,
    staff: EXCEPT_OWNER_ONLY,
    /* Sells. Owns the lead through to the quote, and reads the money rather
       than touching it. */
    sales: [
        'leads:view',
        'leads:edit',
        'quotes:view',
        'quotes:create',
        'quotes:send',
        'clients:view',
        'clients:create',
        'projects:view',
        'invoices:view',
        'care-plans:view',
        'testimonials:view',
        'analytics:view',
    ],
    /* Builds. Owns the project and the blog; has no reason to see an invoice. */
    developer: [
        'leads:view',
        'clients:view',
        'projects:view',
        'projects:create',
        'projects:edit',
        'edit-requests:view',
        'edit-requests:edit',
        'posts:view',
        'posts:create',
        'posts:edit',
        'posts:delete',
        'testimonials:view',
    ],
    /* Reads the money. **Cannot yet raise an invoice or change a care plan**,
       because both are owner-only and were before this map existed — see
       OWNER_ONLY_PERMISSIONS. Widening that is the owner's call, not a
       side-effect of adding a role. */
    accounts: [
        'clients:view',
        'projects:view',
        'quotes:view',
        'invoices:view',
        'care-plans:view',
        'analytics:view',
    ],
    /* Answers the customer. Lives in the change requests. */
    support: [
        'leads:view',
        'clients:view',
        'projects:view',
        'care-plans:view',
        'edit-requests:view',
        'edit-requests:edit',
        'testimonials:view',
        'testimonials:approve',
    ],
};
/**
 * Whether a set of granted permissions includes one. The only way to ask, on
 * either side.
 *
 * It takes the PERMISSIONS, not the role. There used to be a `can(role, …)`
 * that looked the role up in the map above, and after slice 1.4 that function
 * would have been answering from a snapshot — right until the first time
 * somebody edited the matrix, and silently wrong afterwards. The grants now
 * travel with the request: `requireAuth` already re-reads the account on every
 * call, so the role's permissions come back in that same query and
 * `req.admin.permissions` is what the session actually has, with no cache to
 * go stale and no second round trip. The dashboard gets the same list from
 * `/api/auth/me`.
 *
 * An empty list grants nothing, which is also what an unknown role produces —
 * fail-closed, the same rule as a missing `tv` claim.
 */
export function holds(granted, permission) {
    return granted ? granted.includes(permission) : false;
}
/**
 * Lead pipeline stages, in order.
 *
 * `brief` and `preview` arrived in slice 1.5, which widened this to the
 * pipeline the brief specifies: new → contacted → brief received → preview
 * sent → quote sent → won/lost. The ORDER is load-bearing — `nextStatus()`
 * reads the index, the board renders in this sequence, and the Postgres enum
 * was altered with `BEFORE 'quoted'` so the database sorts the same way.
 *
 * `quoted` keeps its stored name and is labelled "Quote sent" on the screen.
 * Renaming a value three modules write would have been a data migration
 * bought with nothing.
 */
export const LEAD_STATUSES = [
    'new',
    'contacted',
    'brief',
    'preview',
    'quoted',
    'won',
    'lost',
];
/**
 * The two stages a lead never leaves.
 *
 * "Open" is DERIVED from this rather than listed, for the reason `staff` is
 * derived from "not owner-only": slice 1.5 added two stages, and the one place
 * that held a hand-written list of open statuses —
 * `['new', 'contacted', 'quoted']`, in the analytics count behind the owner's
 * "waiting on somebody" tile — silently stopped counting two of them. A
 * positive list has to be remembered; `notIn` cannot be forgotten.
 */
export const TERMINAL_LEAD_STATUSES = ['won', 'lost'];
/** Still being worked: neither won nor lost. */
export function isOpenLead(status) {
    return !TERMINAL_LEAD_STATUSES.includes(status);
}
/* ============================================================================
   The two-hour reply promise.
   ----------------------------------------------------------------------------
   `/` says "2 ghante mein reply" in as many words, so this is a published
   commitment and not an internal target.

   One definition, here, for the same reason `outstandingInr()` is here: the
   dashboard draws a countdown from it, the breach sweep decides who to alert
   from it, and the analytics screen will report the share met from it. Three
   copies of "is this late" is three answers on a slow afternoon, and the one
   the owner would trust is whichever they happened to be looking at.
   ========================================================================== */
/** The promise, in minutes. Changing it changes the countdown, the breach
    alert and the report together — which is the point of it being here. */
export const REPLY_SLA_MINUTES = 120;
/**
 * When a reply is owed by.
 *
 * Measured from when the enquiry ARRIVED, not from when somebody opened it.
 * A promise measured from the moment we notice is not a promise.
 */
export function replyDeadline(createdAt) {
    return new Date(new Date(createdAt).getTime() + REPLY_SLA_MINUTES * 60_000);
}
export function replyState(lead, now = new Date()) {
    const created = new Date(lead.createdAt).getTime();
    if (lead.firstRepliedAt) {
        const took = Math.round((new Date(lead.firstRepliedAt).getTime() - created) / 60_000);
        return { kind: 'replied', minutes: took, late: took > REPLY_SLA_MINUTES };
    }
    if (!isOpenLead(lead.status))
        return { kind: 'closed' };
    const left = Math.round((created + REPLY_SLA_MINUTES * 60_000 - now.getTime()) / 60_000);
    return left >= 0 ? { kind: 'waiting', minutes: left } : { kind: 'overdue', minutes: -left };
}
/*
 * There was a `SLA_MEASURED_FROM` constant here for one day, and it was wrong
 * by the end of it.
 *
 * The problem it solved is real and has not gone away: `firstRepliedAt`
 * arrived with slice 1.5 and the migration deliberately did not backfill it,
 * because a reply time invented for a lead nobody timed is not evidence. Every
 * lead older than that migration therefore has no reply time and never will,
 * and `replyState` reads an open one of those as `overdue`. Averaged, that is
 * a measurement gap wearing a bad month's clothes.
 *
 * The constant said "IST midnight on the day the column reaches production"
 * and carried an instruction to move it if the migration slipped. It slipped.
 * Nothing in the code could notice, and the first reading would have been
 * wrong in the direction that looks like a failing business.
 *
 * So the floor is DERIVED now: `slaMeasuredFrom()` in the analytics repository
 * reads `finished_at` for that migration out of `_prisma_migrations`. It is
 * the exact instant after which a reply could be recorded, Prisma writes it
 * down already, and it is right on every database — local, CI, production, a
 * restored backup — without anyone editing anything. Same rule as
 * `publishedAt` and `launchedAt`: anything derived stays derived.
 */
/**
 * A part of a whole as a rounded percentage, or null when there is no whole.
 *
 * Null rather than 0, in both callers, because "nothing to measure yet" and
 * "measured, and the answer is none" are different sentences and a new
 * business sees the first one. 0% replied in time and 0% converted are both
 * alarms; neither is true of a dashboard opened on its first morning.
 */
export function sharePercent(part, whole) {
    if (whole === 0)
        return null;
    return Math.round((part / whole) * 100);
}
/**
 * The share of leads answered inside the promise, over leads already decided.
 *
 * The denominator is the whole argument. It counts `replied` and `overdue` and
 * excludes the other two states `replyState` can return, because:
 *
 * - **`waiting` is not a result yet.** A lead that arrived eleven minutes ago
 *   has neither met the promise nor missed it. Counting it as a miss makes the
 *   figure sag every time business comes in and recover by lunchtime, which is
 *   the one behaviour guaranteed to teach the owner to ignore it.
 * - **`closed` was never measured.** A lead won or lost with no recorded
 *   action is a hole in the records — `firstRepliedAt` only sees what passed
 *   through this dashboard, and the owner answers enquiries on their own
 *   phone. Calling that a breach is the same mistake as the backfill.
 *
 * So this reports on what is known, and the screen says how many leads that
 * was. A percentage without its denominator is a number anyone can argue with.
 */
export function slaShare(leads, now = new Date()) {
    let decided = 0;
    let insideSla = 0;
    for (const lead of leads) {
        const state = replyState(lead, now);
        if (state.kind === 'replied') {
            decided += 1;
            if (!state.late)
                insideSla += 1;
        }
        else if (state.kind === 'overdue') {
            decided += 1;
        }
    }
    return { decided, insideSla, percent: sharePercent(insideSla, decided) };
}
/** Plans a lead can ask about. `care` is a subscription, not a build. */
export const PLAN_IDS = ['starter', 'business', 'pro', 'care'];
/** The three plans a PROJECT can be sold on. */
export const PROJECT_PLANS = ['starter', 'business', 'pro'];
/** List prices in rupees, so a form can prefill. The price is still sent
    explicitly: discounts happen, and a silently derived price hides them. */
export const PLAN_PRICES_INR = {
    starter: 4_999,
    business: 9_999,
    pro: 19_999,
};
export const PROJECT_STATUSES = ['brief', 'preview', 'build', 'live'];
export const INVOICE_TYPES = ['advance', 'final', 'care'];
export const INVOICE_STATUSES = ['due', 'paid', 'refunded'];
/** How an invoice was settled when Razorpay was not involved. */
export const SETTLEMENT_METHODS = ['cash', 'upi', 'bank'];
export const CARE_PLAN_STATUSES = ['active', 'paused', 'cancelled'];
export const POST_STATUSES = ['draft', 'scheduled', 'published'];
export const EDIT_REQUEST_STATUSES = ['open', 'done'];
/** What a ProjectEvent can record. */
export const PROJECT_EVENT_KINDS = ['status_changed', 'promised_date_changed'];
/** Named tokens from packages/tokens, never hex. Colour lives in one file. */
export const COVER_COLORS = ['sage', 'marigold', 'panel', 'mist'];
/**
 * The Indian financial year an instant falls in, as `2026-27`.
 *
 * April to March, which is why this is not the calendar year: 5 March 2027 is
 * `2026-27` and 5 April 2027 is `2027-28`. Invoice numbers and quote numbers
 * are both sequential within it, and this is the one definition — the second
 * spelling of a rule like this is the one that drifts, and here the drift is a
 * duplicate number in a GST sequence.
 */
export function financialYearLabel(now = new Date()) {
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${String((year + 1) % 100).padStart(2, '0')}`;
}
/** Indian mobile numbers: ten digits starting 6-9, +91 optional. */
export const INDIAN_MOBILE = /^(?:\+?91[\s-]?)?[6-9]\d{9}$/;
/** A GSTIN: state code, PAN, entity number, a fixed Z, and a checksum. */
export const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
/**
 * Normalise anything a form accepts to E.164 (+91XXXXXXXXXX).
 *
 * Storing the raw string is how one customer becomes three rows. Order
 * matters: strip the trunk prefix BEFORE checking for the 91 country code, or
 * "0 98765 43210" and "091-9876543210" are both rejected.
 *
 * @returns the E.164 string, or null when the input is not a valid Indian mobile
 */
export function toE164(input) {
    let digits = String(input ?? '').replace(/\D/g, '');
    digits = digits.replace(/^0+/, '');
    if (digits.length > 10 && digits.startsWith('91'))
        digits = digits.slice(2);
    if (!/^[6-9]\d{9}$/.test(digits))
        return null;
    return `+91${digits}`;
}
/**
 * Turn a title into a slug, so the editor can suggest one.
 *
 * Devanagari is removed rather than transliterated: a slug of percent-escapes
 * is worse for a URL someone has to read out over the phone.
 */
export function slugify(title) {
    return title
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .slice(0, 120)
        .replace(/^-|-$/g, '');
}
/* ---------------------------------------------------------------- OTP ----
   The client portal's only login. These numbers are shared rather than
   duplicated because the portal draws a countdown from them and the API
   enforces them: a resend button that unlocks at 45s against a server that
   refuses until 60s is a button that answers "thoda ruk kar" to someone who
   already waited. */
/** Six digits. Long enough that three guesses is nothing, short enough to
    read off a notification and type one-handed. */
export const OTP_LENGTH = 6;
/** How long a code stays usable. Long enough for a WhatsApp message to arrive
    on a bad 4G connection, short enough that a shoulder-surfed code is stale. */
export const OTP_TTL_MINUTES = 5;
/** Guesses per code. At 6 digits this leaves a 3-in-a-million chance, and the
    fourth attempt is refused rather than throttled. */
export const OTP_MAX_ATTEMPTS = 3;
/** Between one code and the next for the same number. */
export const OTP_RESEND_COOLDOWN_SECONDS = 60;
/** Codes per number per hour. The cap is per PHONE and lives in the database,
    not in the in-memory rate limiter, because the limiter is per-instance and
    this has to hold across all of them. */
export const OTP_MAX_PER_HOUR = 5;
/**
 * An invoice total with GST, in rupees. What the client actually pays.
 *
 * Here rather than in either dashboard because the admin shows it on the
 * invoice book and the portal shows it to the person paying, and two copies of
 * a tax calculation is how the office and the customer end up quoting
 * different totals at each other. The API computes it for the portal DTO from
 * this same function.
 */
export function grossInr(invoice) {
    return Math.round(invoice.amountInr * (1 + invoice.gstPercent / 100));
}
/**
 * What is still owed across a set of invoices, in rupees, GST included.
 *
 * One definition, because there were three and two of them disagreed. The
 * office's invoice book and the client's detail screen both summed
 * `amountInr`; the portal summed `grossInr`. Both render the words "baaki
 * hai", so a ₹5,000 + 18% invoice read ₹5,000 in the office and ₹5,900 on the
 * customer's phone — for the same debt, which is the argument on the phone
 * that `grossInr` living in one file was supposed to prevent.
 *
 * **Gross is the correct one.** The client owes the invoice total, GST and
 * all; the net figure is what we keep after remitting, which is a different
 * question and not the one "baaki hai" asks.
 *
 * Only `due` counts. A refunded invoice is money that came back rather than
 * money owed, and counting it would overstate that client's balance for good.
 * Rounded per invoice and then summed, so this agrees to the rupee with the
 * per-row figures printed beside it.
 */
export function outstandingInr(invoices) {
    return invoices
        .filter((invoice) => invoice.status === 'due')
        .reduce((total, invoice) => total + grossInr(invoice), 0);
}
//# sourceMappingURL=constants.js.map